import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// World-class component partner brands — all 9 use official brand logos.
const BRANDS = [
  { name: "Bonfiglioli",        src: "/components/brand-bonfiglioli.png" },
  { name: "ABB",                src: "/components/brand-abb.png" },
  { name: "Schneider Electric", src: "/components/brand-schneider.png" },
  { name: "Usha Martin",        src: "/components/brand-usha-martin.png" },
  { name: "FAG",                src: "/components/brand-fag.png" },
  { name: "Elecon",             src: "/components/brand-elecon.png" },
  { name: "Polycab",            src: "/components/brand-polycab.png" },
  { name: "Kirloskar Engines",  src: "/components/brand-kirloskar.png" },
  { name: "Crompton",           src: "/components/brand-crompton.png" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

function BrandLogo({ brand }) {
  return (
    <img
      src={brand.src}
      alt={`${brand.name} logo`}
      loading="lazy"
      className="max-w-full max-h-16 md:max-h-20 object-contain transition-transform duration-500 group-hover:scale-105"
    />
  );
}

export default function TrustedComponentsSection() {
  return (
    <section
      data-testid="trusted-components"
      className="relative bg-[#0B0B0B] py-24 md:py-32"
    >
      {/* Thin orange accent line at the top of the section */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: "#FF6A00" }}
      />

      <div className="container-x">
        {/* Heading block */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <div className="heading-eyebrow mb-4">Trusted Components</div>
          <h2
            data-testid="trusted-heading"
            className="display-2 text-liftile-cream"
          >
            Engineered With <span className="text-liftile-orange">World-Class Components</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-5 text-base md:text-lg leading-relaxed max-w-2xl">
            Every LIFTILE machine is built using carefully selected components from
            globally trusted brands to ensure maximum reliability, safety, durability
            and long service life.
          </p>
        </motion.div>

        {/* Logo grid — 2 cols mobile, 3 tablet & desktop (perfect 3×3) */}
        <div
          data-testid="trusted-grid"
          className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto"
        >
          {BRANDS.map((brand, i) => (
            <motion.div
              key={brand.name}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              transition={{ delay: i * 0.05 }}
              data-testid={`trusted-card-${brand.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-white rounded-xl shadow-[0_4px_18px_-8px_rgba(0,0,0,0.55)] hover:shadow-[0_18px_45px_-12px_rgba(255,106,0,0.45)] border-2 border-transparent hover:border-liftile-orange transition-all duration-400 ease-out hover:-translate-y-1.5 aspect-[5/3] flex items-center justify-center p-7"
              title={brand.name}
            >
              <BrandLogo brand={brand} />
            </motion.div>
          ))}
        </div>

        {/* Bottom trust statement + CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mt-16 md:mt-20 flex flex-col items-center text-center"
        >
          <p
            data-testid="trust-statement"
            className="font-display text-2xl md:text-4xl text-liftile-cream leading-snug max-w-3xl"
          >
            Premium components are not an option.
            <br />
            <span className="text-liftile-orange">They are our standard.</span>
          </p>
          <Link
            to="/products"
            data-testid="trusted-cta"
            className="group mt-9 inline-flex items-center gap-3 bg-liftile-orange text-white font-heading text-xs md:text-sm uppercase tracking-wider font-semibold px-8 py-4 rounded-sm hover:bg-liftile-matte-black hover:text-liftile-cream hover:shadow-[0_15px_40px_-10px_rgba(0,0,0,0.8)] transition-all duration-300"
          >
            View Our Machines
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
